/**
 * SideStack — renders visible SIDE_PANELS as a column of collapsible sections.
 *
 * Reads the registry, filters by source + showWhen, sorts by order, and
 * renders each panel's component with the appropriate `value`. Panels with
 * `source: 'state:<key>'` receive `appState[key]`; other sources receive
 * `undefined` (the panel fetches its own data).
 */

import { useState } from "react";
import type { AppState } from "@strands/agent-transport";
import { SIDE_PANELS, type SidePanelEntry } from './sidePanels';

interface Props {
  state: AppState;
  sessionId: string | null;
}

function resolveValue(panel: SidePanelEntry, state: AppState): unknown {
  if (panel.source.startsWith('state:')) {
    return state[panel.source.slice(6)];
  }
  return undefined;
}

function isVisible(panel: SidePanelEntry, value: unknown): boolean {
  if (panel.showWhen) return panel.showWhen(value);
  return true;
}

function ChevronsIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={direction === 'left' ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path d="M6 17L11 12L6 7" />
      <path d="M13 17L18 12L13 7" />
    </svg>
  );
}

export function SideStack({ state, sessionId }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const visible = SIDE_PANELS
    .map((panel) => ({ panel, value: resolveValue(panel, state) }))
    .filter(({ panel, value }) => isVisible(panel, value))
    .sort((a, b) => (a.panel.order ?? 0) - (b.panel.order ?? 0));

  if (collapsed) {
    return (
      <aside className="details-card details-card--collapsed">
        <button
          className="details-collapse-btn"
          onClick={() => setCollapsed(false)}
          title="Развернуть панель"
        >
          <ChevronsIcon direction="left" />
        </button>
      </aside>
    );
  }

  return (
    <aside className="details-card">
      <div className="details-header">
        <button
          className="details-collapse-btn"
          onClick={() => setCollapsed(true)}
          title="Свернуть панель"
        >
          <ChevronsIcon direction="right" />
        </button>
        <span className="details-title">Детали</span>
      </div>
      {visible.map(({ panel, value }) => {
        const Component = panel.component;
        return <Component key={panel.id} value={value} state={state} sessionId={sessionId} />;
      })}
    </aside>
  );
}
